import type { LifecycleStatus, EntityType } from './enums';
import type { ListScopeStored } from '@/utils/listScope';
import type { MasterRegulatoryCode } from './compliance';
import type { Regulator } from './regulators';

/** A regulatory code as returned by the codes catalog (id + human-readable code/description). */
export type RegulatoryCode = MasterRegulatoryCode;

/** Read model for an indicator's regulatory-code mapping (embedded per indicator on load).
 *  Effective set = following ? default − excluded ∪ added : added. */
export interface IndicatorRegulationCodesView {
  follow_default: boolean;
  default_codes: RegulatoryCode[];
  added_codes: RegulatoryCode[];
  excluded_code_ids: string[];
}

/** Desired regulatory-code state for one indicator, staged with the rest of a draft edit. */
export interface IndicatorRegulationCodesEdit {
  follow_default: boolean;
  added_code_ids: string[];
  excluded_code_ids: string[];
}

/** Editable per-indicator working copy of the codes mapping (edit mode). Holds full code
 *  objects for rendering; the draft-edit payload derives the id lists from these. */
export interface IndicatorCodesDraft {
  followDefault: boolean;
  defaultCodes: RegulatoryCode[];
  addedCodes: RegulatoryCode[];
  excludedCodeIds: string[];
}

export interface KytIndicator {
  id: string;
  block_id: string;
  name?: string;
  code?: string;
  display_code?: string;
  indicator_slug: string;
  entity_type: EntityType;
  config_data_json: Record<string, any>;
  indicator_hash: string;
  inserted_at: string;
  regulation_codes?: IndicatorRegulationCodesView | null;
}

export interface KytInstance {
  id: string;
  version_number: number | null;
  version_instance_lifecycle: LifecycleStatus;
  published_at: string | null;
  inserted_at: string;
  indicators: KytIndicator[];
  alerts?: KytPolicyAlert[];
}

export interface KytPolicy {
  id: string;
  name: string;
  indicator_code_prefix: string;
  alert_code_prefix: string;
  is_template: boolean;
  is_active: boolean;
  activated_at: string | null;
  current_instance_id: string | null;
  draft_id: string | null;
  regulator?: Regulator | null; // the single regulator this policy reports to
  inserted_at: string;
  current_instance_author_name?: string | null; // who created the current published version; list endpoint only
  updated_at: string;
  deleted_at: string | null;

  // Nested for detail views
  current_instance?: KytInstance;
  draft?: KytInstance;
}

/** Value shape of an indicator's risk factor (drives UI formatting / validation). */
export enum KytRiskFactorValueType {
  PROPORTION = 'proportion',
  MULTIPLIER = 'multiplier',
  COUNT = 'count',
  MONEY = 'money',
  HHI = 'hhi',
}

/**
 * User-configurable risk-factor breakpoints for the score bands. Upper bounds are inclusive.
 * `score_1_until` is the floor (raw value at/below it → the minimum score, 1) and `score_100_at`
 * the ceiling (→ 100). very_low (score 0) is reserved to future watchlist logic and has no
 * configurable threshold here. Keep in sync with backend `LEVEL_THRESHOLD_KEYS`.
 */
export interface KytLevelThresholds {
  score_1_until: number;
  low_max: number;
  medium_max: number;
  high_max: number;
  score_100_at: number;
}

/** Shape of a risk-factor parameter's stored value. Extend as new param kinds are added. */
export enum KytRiskFactorParamType {
  WINDOW = 'window', // value shape: { unit, value }
  CHOICE = 'choice', // value: string (one of options[].value)
  NUMBER = 'number', // value: number (bounded by min/max)
  MULTISELECT = 'multiselect', // value: string[] (subset of options[].value); [] == all
  LIST_SCOPE = 'list_scope', // value: { mode: all|include|exclude, lists: [...] } — see utils/listScope
}

/** Per-segment score bands, keyed by segment value (mei/me/epp/young/other/revenue_predictions). */
export type KytSegmentThresholds = Record<string, KytLevelThresholds>;

/** One segment's default band — an element of the segmented `default_thresholds`. */
export interface KytSegmentBand {
  segment: string;
  label: string;
  /** The fallback band that must stay configured (only `other`); others may be removed. */
  required: boolean;
  /** Per-band value type override (e.g. revenue_predictions = multiplier); undefined = money-ish default. */
  value_type?: KytRiskFactorValueType | null;
  unit?: string | null;
  thresholds: KytLevelThresholds;
}

/**
 * Segmented default_thresholds, keyed by entity type: `{ company: [...], person: [...] }`.
 * (A flat `KytSegmentBand[]` is also accepted for back-compat.)
 */
export type KytSegmentBandsByEntity = Record<string, KytSegmentBand[]>;

/** A time window: how far back transactions are aggregated for the factor. */
export interface KytTransactionWindow {
  unit: string; // "day" (extensible: "week" / "month")
  value: number;
}

/** Stored value of a risk-factor parameter. Shape depends on the param's `type`. */
export type KytRiskFactorParamValue = KytTransactionWindow | string | number | string[] | ListScopeStored;

/** A selectable option for a `choice` / `multiselect` param. */
export interface KytRiskFactorParamChoice {
  value: string;
  label: string;
}

/** Conditional-visibility rule: only show this param when `key` currently equals `value`. */
export interface KytRiskFactorParamDependency {
  key: string;
  value: unknown;
}

/** Catalog definition of a tunable risk-factor parameter (drives the config UI). */
export interface KytRiskFactorParamOption {
  key: string;
  label: string;
  description: string;
  type: KytRiskFactorParamType;
  /** Default value, shape depends on `type` (window object / string / number / string[]). */
  default: KytRiskFactorParamValue;
  /** Window options. */
  units?: string[];
  values?: number[];
  /** choice / multiselect options. */
  options?: KytRiskFactorParamChoice[];
  /** number bounds. */
  min?: number | null;
  max?: number | null;
  step?: number | null;
  /** Only render this param when another param currently holds a given value. */
  depends_on?: KytRiskFactorParamDependency | null;
}

export interface KytRiskFactorOption {
  name: string;
  description: string;
  /** None for a mixed indicator (per-band value_type, e.g. financial_capacity). */
  value_type?: KytRiskFactorValueType | null;
  unit: string;
  value_suffix?: string;
  min?: number | null;
  max?: number | null;
  params?: KytRiskFactorParamOption[];
  default_thresholds: KytLevelThresholds | KytSegmentBand[] | KytSegmentBandsByEntity;
}

export interface KytIndicatorOption {
  slug: string;
  name: string;
  available_entity_types: EntityType[];
  version: number;
  status: string;
  risk_factor?: KytRiskFactorOption | null;
}

/** Typed shape of an indicator's `config_data_json` (config_data_json_version = 1). */
export interface KytIndicatorConfigData {
  score_config?: {
    level_thresholds?: KytLevelThresholds;
    segment_thresholds?: KytSegmentThresholds;
  };
  risk_factor_config?: {
    observation_window?: KytTransactionWindow;
    current_window?: KytTransactionWindow;
    lookback_window?: KytTransactionWindow;
    [key: string]: unknown;
  };
}

export interface KytIndicatorInput {
  id?: string; // Local ID for UI tracking
  slug: string;
  name?: string;
  code?: string; // Existing indicator code; sent for indicators kept across an edit
  display_code?: string;
  entity_type: EntityType;
  config_data_json: Record<string, any>;
  config_data_json_version?: number;
}

/** A threshold for an indicator's alert (bundled with the indicator in a draft edit). */
export interface KytAlertThresholdInput {
  metric_type: KytAlertMetricType;
  value: number;
  name?: string;
}

/** A brand-new indicator added while editing a draft, with its optional alert. */
export interface KytDraftIndicatorAdd {
  slug: string;
  name?: string;
  entity_type: EntityType;
  config_data_json: Record<string, any>;
  config_data_json_version?: number;
  alert?: KytAlertThresholdInput | null;
  regulation_codes?: IndicatorRegulationCodesEdit | null;
}

/** An existing draft indicator whose fields and/or alert changed. `id` is the draft row id. */
export interface KytDraftIndicatorUpdate {
  id: string;
  name?: string;
  entity_type: EntityType;
  config_data_json: Record<string, any>;
  config_data_json_version?: number;
  alert?: KytAlertThresholdInput | null;
  regulation_codes?: IndicatorRegulationCodesEdit | null;
}

/** Explicit per-item changes to apply to a draft. Unlisted rows are left untouched. */
export interface KytDraftEditPayload {
  added: KytDraftIndicatorAdd[];
  updated: KytDraftIndicatorUpdate[];
  removed_ids: string[];
  global_alert?: KytGlobalAlertInput;
}

export interface KytPolicyCreatePayload {
  name: string;
  publish: boolean;
  is_active: boolean;
  is_template?: boolean;
  based_on_policy_id?: string;
  indicator_code_prefix?: string;
  alert_code_prefix?: string;
  indicators: KytIndicatorInput[];
  indicator_alerts?: KytIndicatorAlertInput[];
  global_alert?: KytGlobalAlertInput;
}

export interface KytPolicyPrefixUpdatePayload {
  indicator_code_prefix: string;
  alert_code_prefix: string;
}

export interface KytPolicyNameUpdatePayload {
  name: string;
}

export interface KytToggleActivePayload {
  is_active: boolean;
}

/** How an alert threshold is evaluated (pipeline support for risk_level coming later). */
export enum KytAlertMetricType {
  SCORE = 'score',
  RISK_LEVEL = 'risk_level',
}

/** Maps to `KytPolicyAlert.template` in Postgres. */
export enum KytAlertTemplate {
  SIMPLE_SINGLE_INDICATOR = 'simple_single_indicator',
  GLOBAL = 'global',
}

export interface KytAlertThresholdDraft {
  metricType: KytAlertMetricType;
  value: number;
  name?: string;
}

export interface KytIndicatorAlertDraft extends KytAlertThresholdDraft {
  indicatorLocalId: string;
  enabled: boolean;
}

export interface KytGlobalAlertDraft extends KytAlertThresholdDraft {
  enabled: boolean;
}

export interface KytAlertConfigDataJson {
  metric_type: KytAlertMetricType;
  value: number;
  indicator_code?: string;
  indicator_codes?: string[];
}

export interface KytPolicyAlert {
  id: string;
  block_id: string;
  code: string;
  name?: string;
  display_code?: string;
  template: KytAlertTemplate;
  config_data_json: KytAlertConfigDataJson;
  config_data_json_version?: number | null;
  inserted_at: string;
}

export interface KytIndicatorAlertInput {
  indicator_index: number;
  enabled: boolean;
  metric_type: KytAlertMetricType;
  value: number;
  name?: string;
}

export interface KytGlobalAlertInput {
  enabled: boolean;
  metric_type: KytAlertMetricType;
  value: number;
  name?: string;
}
